import { ArrowLeftIcon } from "@heroicons/react/outline";
import { UserCircleIcon } from "@heroicons/react/solid";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../../../../components/Sidebar/Sidebar";
import { apiV3 } from "../../../../utils/helpers/api";
import { classNames } from "../../../../utils/helpers/misc";
import { useToast } from "../../../../components/Toastr/Toastr";
import { months } from "../../../../utils/helpers/misc";

export default function SelfServeAuditV3() {
  const { showErrorToast } = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [selfServeAudit, setSelfServeAudit] = useState<any>(null);
  const [faceImageUrl, setFaceImageUrl] = useState<string>("");
  const [idImageUrl, setIdImageUrl] = useState<string>("");

  useEffect(() => {
    window.scrollTo(0, 0);
    // Get selfServeAudit from database
    const selfServeAuditId = params.selfServeAuditId;
    if (selfServeAuditId) {
      fetchSelfServeAudit(selfServeAuditId);
    } else {
      showErrorToast();
    }
  }, []);

  const fetchSelfServeAudit = async (selfServeAuditId: string) => {
    try {
      setLoading(true);
      const res = await apiV3.getSelfServeAuditById(selfServeAuditId);
      const selfServeAudit = res.data;
      setSelfServeAudit(selfServeAudit);
      setLoading(false);

      // Get images
      const getImagesPromises = [];

      if (selfServeAudit.faceImage) {
        const faceImagePromise = apiV3.getSelfServeAuditFileById(
          selfServeAudit.id,
          selfServeAudit.faceImage
        );
        getImagesPromises.push(faceImagePromise);
      }

      if (selfServeAudit.idImage) {
        const idImagePromise = apiV3.getSelfServeAuditFileById(
          selfServeAudit.id,
          selfServeAudit.idImage
        );
        getImagesPromises.push(idImagePromise);
      }

      const [faceImageRes, idImageRes] = await Promise.all(getImagesPromises);

      if (faceImageRes && faceImageRes.data.url) {
        setFaceImageUrl(faceImageRes.data.url);
      }

      if (idImageRes && idImageRes.data.url) {
        setIdImageUrl(idImageRes.data.url);
      }
    } catch (error) {
      console.error(
        `Error occurred while fetching selfServeAudit with id ${selfServeAuditId}`,
        error
      );
      showErrorToast();
    }
  };

  return (
    <>
      <Sidebar />
      <div className="md:pl-64 flex flex-col flex-1">
        <main className="flex-1">
          {/* Replace with your content */}
          <div className="py-4">
            {selfServeAudit && !loading ? (
              <>
                <div className="mx-auto max-w-3xl px-4 sm:px-6 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl lg:px-8">
                  <div className="flex items-center space-x-5">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 items-center flex flex-row">
                        <UserCircleIcon className="h-8 w-8 mr-1.5" />
                        {selfServeAudit.firstName + " " + selfServeAudit.lastName}
                      </h1>
                    </div>
                  </div>
                  <div className="justify-stretch mt-6 flex flex-col-reverse space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-y-0 sm:space-x-3 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
                    <button
                      onClick={() => navigate(-1)}
                      type="button"
                      className="inline-flex items-center justify-center rounded-md border border-transparent bg-gray-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:w-auto"
                    >
                      <ArrowLeftIcon className="h-5 w-5 mr-1.5" />
                      Back
                    </button>
                  </div>
                </div>

                <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
                  <div className="space-y-6 lg:col-span-2 lg:col-start-1">
                    {/* Description list*/}
                    <section aria-labelledby="applicant-information-title">
                      <div className="bg-white shadow sm:rounded-lg">
                        <div className="px-4 py-5 sm:px-6">
                          <h2
                            id="applicant-information-title"
                            className="text-lg font-medium leading-6 text-gray-900"
                          >
                            Self Serve Audit
                          </h2>
                          <p className="mt-1 max-w-2xl text-sm text-gray-500">
                            Submitted on{" "}
                            {new Date(selfServeAudit.submittedOn).toLocaleString("en-AU")}
                          </p>
                        </div>
                        <div className="border-t border-gray-200 px-4 py-5 sm:px-6 flex flex-row">
                          <div className="flex flex-col mr-4">
                            <div>
                              <img
                                className="rounded-md w-64 mb-2"
                                src={
                                  faceImageUrl
                                    ? faceImageUrl
                                    : "https://i1.wp.com/www.slntechnologies.com/wp-content/uploads/2017/08/ef3-placeholder-image.jpg?ssl=1"
                                }
                              />
                            </div>
                            <div>
                              <img
                                className="rounded-md w-64"
                                src={
                                  idImageUrl
                                    ? idImageUrl
                                    : "https://i1.wp.com/www.slntechnologies.com/wp-content/uploads/2017/08/ef3-placeholder-image.jpg?ssl=1"
                                }
                              />
                            </div>
                          </div>
                          <div>
                            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                              <div className="sm:col-span-1">
                                <dt className="text-sm font-semibold text-gray-500">ASIC ID</dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                  {selfServeAudit.asicId}
                                </dd>
                              </div>
                              <div className="sm:col-span-1">
                                <dt className="text-sm font-semibold text-gray-500">Company</dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                  {selfServeAudit.company}
                                </dd>
                              </div>
                              <div className="sm:col-span-1">
                                <dt className="text-sm font-semibold text-gray-500">Expiry Date</dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                  {(selfServeAudit.asicExpiryDate
                                    ? months[new Date(selfServeAudit.asicExpiryDate).getUTCMonth()]
                                    : "") +
                                    " " +
                                    new Date(selfServeAudit.asicExpiryDate).getUTCFullYear()}
                                </dd>
                              </div>
                              <div className="sm:col-span-1">
                                <dt className="text-sm font-semibold text-gray-500">
                                  Airport Code
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                  {selfServeAudit.airportCode}
                                </dd>
                              </div>
                              <div className="sm:col-span-1">
                                <dt className="text-sm font-semibold text-gray-500">
                                  Access Level
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                  {selfServeAudit.accessLevel}
                                </dd>
                              </div>
                              <div className="sm:col-span-1">
                                <dt className="text-sm font-semibold text-gray-500">
                                  Access Location
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                  {selfServeAudit.accessLocation}
                                </dd>
                              </div>
                              <div className="sm:col-span-1">
                                <dt className="text-sm font-semibold text-gray-500">Face Match</dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                  <div
                                    className={classNames(
                                      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                                      parseFloat(selfServeAudit.faceMatchScore) > 50
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                                    )}
                                  >
                                    {(+parseFloat(selfServeAudit.faceMatchScore).toFixed(
                                      2
                                    )).toString() + "%"}
                                  </div>
                                </dd>
                              </div>
                              {selfServeAudit.submittedLatitude &&
                                selfServeAudit.submittedLongitude && (
                                  <div className="sm:col-span-1">
                                    <dt className="text-sm font-semibold text-gray-500">
                                      Submitted From
                                    </dt>
                                    <dd className="mt-1 text-sm text-gray-900">
                                      ({selfServeAudit.submittedLatitude},{" "}
                                      {selfServeAudit.submittedLongitude})
                                    </dd>
                                  </div>
                                )}
                              <div className="sm:col-span-1">
                                <dt className="text-sm font-semibold text-gray-500">
                                  Operational Requirement
                                </dt>
                                <dd className="mt-1 text-sm text-gray-900">
                                  {selfServeAudit.operationalRequirement}
                                </dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-sm mt-2">Loading...</div>
            )}
          </div>
        </main>
      </div>
      <div className="min-h-full">
        <main className="py-10">{/* Page header */}</main>
      </div>
    </>
  );
}
